import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { appThemeColors } from "styles/appTheme";
import { appThemeFonts } from "styles/appThemeFonts";
import { appThemeRadius } from "styles/appThemeTokens";
import { dataTableSectionSx } from "common/Components/DataTable/DataTable.styles";
import {
  DATA_TABLE_TOOLBAR_CONTAINER,
  toolbarIconOnlyButtonSx,
} from "common/Components/DataTable/tableToolbarButton.styles";

const uiFont = appThemeFonts.family.ui;
const numericFont = appThemeFonts.family.numeric;

export const TagsPageRoot = styled(Box)({
  ...dataTableSectionSx,
  fontFamily: uiFont,
  backgroundColor: "transparent",
});

export const TagsTableFrame = styled(Box)({
  ...dataTableSectionSx,
});

export const TagsCountBadge = styled("span")({
  fontFamily: numericFont,
  fontSize: "11.5px",
  color: appThemeColors.text.primary,
  border: `1px solid ${appThemeColors.border.strong}`,
  borderRadius: appThemeRadius.pill,
  padding: "2px 9px",
  whiteSpace: "nowrap",
  lineHeight: 1.4,
});

export const TagTypeChipStyled = styled(Chip)(() => ({
  fontWeight: 500,
  fontFamily: uiFont,
}));

export const TagsReadOnlyCellText = styled(Typography)(() => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontFamily: uiFont,
  fontSize: "12.5px",
  color: appThemeColors.text.primary,
  lineHeight: 1.35,
}));

/** Primary emphasis — Node Name (mock `textCell`). */
export const TagsNodeNameCellText = styled(TagsReadOnlyCellText)(() => ({
  color: appThemeColors.text.primary,
}));

/** Dim secondary values — connector, type, subtype (mock `dimCell`). */
export const TagsDimCellText = styled(TagsReadOnlyCellText)(() => ({
  color: appThemeColors.text.muted,
}));

/** Path / alias mono-style muted values (mock `monoCell`). */
export const TagsMonoCellText = styled(Typography)(() => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontFamily: numericFont,
  fontSize: "12px",
  color: appThemeColors.text.muted,
  lineHeight: 1.35,
}));

export const TagsAliasPathText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$contextualized",
})(({ $contextualized }) => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontFamily: numericFont,
  fontSize: "12px",
  lineHeight: 1.35,
  color: appThemeColors.text.muted,
  fontStyle: $contextualized ? "normal" : "italic",
}));

export const TagsScopeTabs = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 18,
  flex: "0 0 auto",
  marginLeft: 12,
  [`@container ${DATA_TABLE_TOOLBAR_CONTAINER} (max-width: 1040px)`]: {
    gap: 12,
    marginLeft: 8,
  },
  [`@container ${DATA_TABLE_TOOLBAR_CONTAINER} (max-width: 880px)`]: {
    gap: 10,
  },
});

export const TagsScopeTab = styled("button", {
  shouldForwardProp: (prop) => prop !== "$active",
})(({ $active }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  padding: "8px 4px",
  cursor: "pointer",
  fontFamily: uiFont,
  fontSize: "13px",
  fontWeight: $active ? 600 : 400,
  whiteSpace: "nowrap",
  background: "none",
  border: 0,
  borderBottom: `2px solid ${
    $active ? appThemeColors.text.lightBlue : "transparent"
  }`,
  color: $active ? appThemeColors.text.heading : appThemeColors.text.muted,
  "&:hover": {
    color: $active
      ? appThemeColors.text.lightBlue
      : appThemeColors.text.heading,
  },
}));

export const TagsScopeTabBadge = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 20,
  height: 18,
  padding: "0 6px",
  borderRadius: appThemeRadius.pill,
  fontSize: "11px",
  fontWeight: 600,
  backgroundColor: appThemeColors.background.fill,
  color: appThemeColors.text.primary,
});

export const ToolbarClearFiltersButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "$active",
})(({ $active }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  height: 34,
  minHeight: 34,
  padding: "0 14px",
  backgroundColor: appThemeColors.background.paper,
  border: `1px solid ${
    $active ? appThemeColors.border.strong : appThemeColors.border.default
  }`,
  borderRadius: appThemeRadius.pill,
  color: $active
    ? appThemeColors.text.primary
    : appThemeColors.text.faint,
  fontSize: "12.5px",
  fontWeight: 500,
  fontFamily: uiFont,
  textTransform: "none",
  whiteSpace: "nowrap",
  boxShadow: "none",
  lineHeight: 1.2,
  opacity: $active ? 1 : 0.55,
  cursor: $active ? "pointer" : "default",
  "& .MuiButton-startIcon": {
    marginRight: 0,
    marginLeft: 0,
    "& > *:nth-of-type(1)": {
      fontSize: 14,
    },
  },
  "&:hover": $active
    ? {
        backgroundColor: appThemeColors.background.kbd,
        borderColor: appThemeColors.text.lightBlue,
        boxShadow: "none",
      }
    : {
        backgroundColor: appThemeColors.background.paper,
        boxShadow: "none",
      },
  "&.Mui-disabled": {
    backgroundColor: appThemeColors.background.paper,
    border: `1px solid ${appThemeColors.border.default} !important`,
    color: `${appThemeColors.text.faint} !important`,
    WebkitTextFillColor: appThemeColors.text.faint,
    opacity: 0.55,
  },
  ...toolbarIconOnlyButtonSx,
}));
